import { Injectable } from '@angular/core';
import { ComponentHelperService } from '@features/forms/services/component-helper/component-helper.service';
import { I18nService } from '@yourcause/common/i18n';
import { NotifierService } from '@yourcause/common/notifier';
import { AttachYCState, BaseYCService } from '@yourcause/common/state';
import { FormSuggestionResources } from './form-suggestions.resources';
import { uniq } from 'lodash';
import { SpellCheckAttributes, SpellCheckCorrectionForUi, SpellCheckCorrectionFromApi, Spell_Check_Attributes } from '../../form-builder.typing';
import { FormDefinitionComponent } from '@features/configure-forms/form.typing';
import { ConfirmAndTakeActionService } from '@yourcause/common/modals';
import { FormSuggestionsState } from './form-suggestions.state';
import { FormBuilderService } from '../form-builder/form-builder.service';
import { FlyoutService } from '@yourcause/common/flyout';
import { FormSuggestionsFlyoutComponent } from '../../form-suggestions-flyout/form-suggestions-flyout.component';
import { AppShellService } from '@core/services/app-shell.service';

@AttachYCState(FormSuggestionsState)
@Injectable({ providedIn: 'root' })
export class FormSuggesetionsService extends BaseYCService<FormSuggestionsState> {

  constructor (
    private componentHelper: ComponentHelperService,
    private i18n: I18nService,
    private notifier: NotifierService,
    private formSuggestionResources: FormSuggestionResources,
    private confirmAndTakeAction: ConfirmAndTakeActionService,
    private formBuilderService: FormBuilderService,
    private flyoutService: FlyoutService,
    private appShellService: AppShellService
  ) {
    super();
  }
 
  get formSuggestions () {
    return this.get('formSuggestions');
  }

  get flyoutIsOpen () {
    return this.get('flyoutIsOpen');
  }

  get selectedComponent () {
    return this.get('selectedComponent');
  }

  /**
   * Do AI Spell Check
   *
   * @returns the components that have spelling updates
   */
  async doSpellCheck () {
    const allComponents = this.componentHelper.getAllComponents(
      this.formBuilderService.currentFormBuilderDefinition,
      true
    );
    if (allComponents.length === 0) {
      this.notifier.error(this.i18n.translate(
        'error:textAddAtLeastOneCompToSpellCheck',
        {},
        'Add at least one component to run the spell check.'
      ));

      return null;
    } else {
      const adaptedComps = allComponents.reduce<string[]>((acc, comp) => {
        if (this.componentHelper.isLayoutComponent(comp.type)) {
          if (comp.type === 'panel' || comp.type === 'fieldset') {
            return [
              ...acc,
              comp[comp.type === 'panel' ? 'title' : 'legend']
            ].filter((item) => !!item);
          } else {
            return acc;
          }
        }

        return [
          ...acc,
          comp.hideLabel ? '' : comp.label,
          comp.description,
          comp.placeholder,
          comp.tooltip
        ].filter((item) => !!item);
      }, []);
      
      const corrections = await this.formSuggestionResources.doSpellCheckOfFormComponents(uniq(adaptedComps));

      return this.adaptCorrectionsFromApi(corrections, allComponents);
    }
  }

  /**
   * Adapt the corrections from the API to the format used in the UI
   * 
   * @param corrections: The corrections from the API
   * @param allComponents: The components in the form builder definition
   * @returns the adapted corrections for the UI
   */
  adaptCorrectionsFromApi (corrections: SpellCheckCorrectionFromApi[], allComponents: FormDefinitionComponent[]) {
    const correctionsMap = corrections.reduce((acc, item) => {
      if (!acc[item.original] && (item.original !== item.correction)) {
        return {
          ...acc,
          [item.original]: item.correction
        }
      }

      return {
        ...acc
      };
    }, {} as Record<string, string>);
    
    return allComponents.filter((component) => {
      return Spell_Check_Attributes.some((attribute) => {
        return !!correctionsMap[component[attribute]];
      });
    }).reduce((acc, component) => {
      let returnVal = [
        ...acc
      ];
      Spell_Check_Attributes.forEach((attribute) => {
        if (!!correctionsMap[component[attribute]]) {
          returnVal = [
            ...returnVal,
            this.getAdaptedCorrection(component, attribute, correctionsMap)
          ];
        }
      });

      return returnVal;
    }, [] as SpellCheckCorrectionForUi[]);
  }

  getAdaptedCorrection (
    component: FormDefinitionComponent,
    attribute: SpellCheckAttributes,
    correctionsMap: Record<string, string>
  ) {
    return {
      attribute,
      original: component[attribute],
      correction: correctionsMap[component[attribute]],
      component,
      selected: true,
      truncatedCorrection: this.getTruncatedText(correctionsMap[component[attribute]])
    }
  }


  /**
   * Truncate the text to a certain length
   * 
   * @param text: Text to check for truncation
   * @returns truncated text if it is longer than 150 characters
   */
  getTruncatedText (text: string) {
    const shouldTruncate = text.length > 150;
    if (shouldTruncate) {
      return text.slice(0, 150) + '...';
    }

    return text;
  }

  /**
   * Handle Spell Checking the Current Form Builder Definition
   */
  async handleSpellCheck () {
    const result = await this.confirmAndTakeAction.genericTakeAction(
      () => this.doSpellCheck(),
      '',
      this.i18n.translate(
        'common:textErrorPerformingSpellCheck',
        {},
        'There was an error performing spell check'
      )
    );
    if (result.passed) {
      this.handleOpenSuggestions(result.endpointResponse);
    }
  }

  /**
   * Handle opening the suggestions flyout
   * 
   * @param corrections: The corrections to be displayed in the flyout
   */
  async handleOpenSuggestions (corrections: SpellCheckCorrectionForUi[]) {
    if (corrections.length === 0) {
      this.notifier.success(this.i18n.translate(
        'common:textLooksGoodNoSuggestionsToReport',
        {},
        'Looks good! No suggestions to report.'
      ));
    } else {
      if (!this.appShellService.sidebarClosed) {
        this.appShellService.toggleSidebar();
      }
      this.set('formSuggestions', corrections);
      this.set('flyoutIsOpen', true);
      await this.flyoutService.openFlyout(
        FormSuggestionsFlyoutComponent,
        {
          showIterator: false,
          maxWidth: 600
        }
      );
      this.set('flyoutIsOpen', false);
      this.set('selectedComponent', null);
    }
  }

  /**
   * Sets the selected component in the suggestions flyout
   * 
   * @param component The component to be selected
   */
  setSelectedComponent (component: FormDefinitionComponent) {
    this.formBuilderService.setComponentToViewOrEdit({
      isEdit: false,
      compType: component.type,
      compKey: component.key
    });
    this.set('selectedComponent', component);
  }

  /**
   * Apply corrections to the form builder definition
   * 
   * @param corrections: The corrections to be applied
   */
  applyChanges (corrections: SpellCheckCorrectionForUi[]) {
    corrections.forEach((correction) => {
      const {
        foundComponent,
        tabIndex
      } = this.componentHelper.findComponentByIdentifier(
        this.formBuilderService.currentFormBuilderDefinition,
        correction.component.key,
        'key'
      );
      this.componentHelper.removeOrReplaceFormComponent(
        foundComponent,
        this.formBuilderService.currentFormBuilderDefinition[tabIndex],
        {
          ...foundComponent,
          [correction.attribute]: correction.correction
        }
      );
    });
    this.flyoutService.closeFlyout();
  }
}
